import type { FC, ReactNode } from "react"
import { Box, Stack, Typography, useTheme } from "@mui/material"
import { Trans, useTranslation } from "react-i18next"

import { TOWER_GLOBAL_LEAN_COLORS } from "helpers/config/dashboards/tower/defs"
import PopUp from "widgets/common/PopUp"
import TowerPositionFromAbove from "widgets/towers/TowerPosition/TowerPositionFromAbove"

import frontalLeanGif from "images/towers/frontal-lean.gif"
import lateralLeanGif from "images/towers/lateral-lean.gif"
import frontalLeanGifEs from "images/towers/frontal-lean-ES.gif"
import lateralLeanGifEs from "images/towers/lateral-lean-ES.gif"

const T_PREFIX = "plots_descriptions.LAST_POSITION_DIALOG"

const EXAMPLE_MAGNITUDE = 10
const EXAMPLE_HEADING = -37

interface TowerLastPositionDialogProps {
  open: boolean
  onClose: () => void
}

const SectionLabel: FC<{ children: string }> = ({ children }) => (
  <Typography variant="h5">{children}</Typography>
)

interface LeanDirectionCardProps {
  title: string
  color: string
  gif: string
  caption: string
}

const LeanDirectionCard: FC<LeanDirectionCardProps> = ({
  title,
  color,
  gif,
  caption,
}) => (
  <Stack
    flex={1}
    sx={{
      border: "1px solid",
      borderColor: (theme) => theme.palette.neutral[400],
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Typography sx={{ color, fontWeight: 700, px: 1.5, pt: 1, pb: 1.5 }}>
      {title}
    </Typography>
    <Box component="img" src={gif} alt={title} sx={{ width: "100%", display: "block" }} />
    <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
      {caption}
    </Typography>
  </Stack>
)

interface LegendItemProps {
  marker?: ReactNode
  label: string
  desc: string
  labelColor?: string
}

const LegendItem: FC<LegendItemProps> = ({ marker, label, desc, labelColor }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    {/* Fixed-width slot the height of one text line, so the marker sits centered
        on the first line of the label rather than floating above it. */}
    <Box
      sx={{
        width: 22,
        height: "1.5em",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {marker}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      <Typography
        component="span"
        variant="body2"
        sx={{ color: labelColor, fontWeight: 700 }}
      >
        {label}
      </Typography>
      {` — ${desc}`}
    </Typography>
  </Stack>
)

export const TowerLastPositionDialog: FC<TowerLastPositionDialogProps> = ({
  open,
  onClose,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const isEs = i18n.resolvedLanguage?.startsWith("es")
  const frontalGif = isEs ? frontalLeanGifEs : frontalLeanGif
  const lateralGif = isEs ? lateralLeanGifEs : lateralLeanGif

  const lineMarker = (color: string, dashed: boolean): ReactNode => (
    <Box
      sx={{
        width: 18,
        borderTop: `2px ${dashed ? "dashed" : "solid"}`,
        borderColor: color,
      }}
    />
  )

  const squareMarker = (filled: boolean): ReactNode => (
    <Box
      sx={{
        width: 13,
        height: 13,
        border: `1.5px ${filled ? "solid" : "dashed"}`,
        borderColor: theme.palette.common.white,
        backgroundColor: filled ? theme.palette.background.paper : "transparent",
      }}
    />
  )

  return (
    <PopUp
      open={open}
      onClose={onClose}
      titleKey={`${T_PREFIX}.TITLE`}
      maxWidth="md"
      fullWidth
    >
      <Stack gap={2} padding={1}>
        <Typography color="text.secondary">
          <Trans i18nKey={`${T_PREFIX}.INTRO`} components={{ b: <strong /> }} />
        </Typography>

        {/* The two lean directions the tower is measured along */}
        <SectionLabel>{t(`${T_PREFIX}.TWO_DIRECTIONS_LABEL`)}</SectionLabel>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <LeanDirectionCard
            title={t(`${T_PREFIX}.FRONT_LEAN_LABEL`)}
            color={TOWER_GLOBAL_LEAN_COLORS.front}
            gif={frontalGif}
            caption={t(`${T_PREFIX}.FRONT_LEAN_CAPTION`)}
          />
          <LeanDirectionCard
            title={t(`${T_PREFIX}.RIGHT_LEAN_LABEL`)}
            color={TOWER_GLOBAL_LEAN_COLORS.right}
            gif={lateralGif}
            caption={t(`${T_PREFIX}.RIGHT_LEAN_CAPTION`)}
          />
        </Stack>

        {/* Diagram + legend, matching the "seen from above" chart on the page */}
        <SectionLabel>{t(`${T_PREFIX}.OVERVIEW_LABEL`)}</SectionLabel>
        <Box
          sx={{
            border: "1px solid",
            borderColor: theme.palette.neutral[400],
            borderRadius: 2,
            p: 2,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems="center">
            <Box sx={{ width: { xs: "100%", md: 340 }, height: 320, flexShrink: 0 }}>
              <TowerPositionFromAbove
                magnitude={EXAMPLE_MAGNITUDE}
                heading={EXAMPLE_HEADING}
                hideLegend
              />
            </Box>
            <Stack gap={1.5} flex={1}>
              <LegendItem
                marker={lineMarker(TOWER_GLOBAL_LEAN_COLORS.front, true)}
                labelColor={TOWER_GLOBAL_LEAN_COLORS.front}
                label={t(`${T_PREFIX}.FRONT_LEAN_LABEL`)}
                desc={t(`${T_PREFIX}.FRONT_LEAN_DESC`)}
              />
              <LegendItem
                marker={lineMarker(TOWER_GLOBAL_LEAN_COLORS.right, true)}
                labelColor={TOWER_GLOBAL_LEAN_COLORS.right}
                label={t(`${T_PREFIX}.RIGHT_LEAN_LABEL`)}
                desc={t(`${T_PREFIX}.RIGHT_LEAN_DESC`)}
              />
              <LegendItem
                marker={lineMarker(TOWER_GLOBAL_LEAN_COLORS.magnitude, false)}
                labelColor={TOWER_GLOBAL_LEAN_COLORS.magnitude}
                label={t(`${T_PREFIX}.MAGNITUDE_LABEL`)}
                desc={t(`${T_PREFIX}.MAGNITUDE_DESC`)}
              />
              <LegendItem
                marker={squareMarker(false)}
                label={t(`${T_PREFIX}.REFERENCE_LABEL`)}
                desc={t(`${T_PREFIX}.REFERENCE_DESC`)}
              />
              <LegendItem
                marker={squareMarker(true)}
                label={t(`${T_PREFIX}.CURRENT_LABEL`)}
                desc={t(`${T_PREFIX}.CURRENT_DESC`)}
              />
              {/* Heading has no distinct mark on the chart (it is the
                    direction of the magnitude line), so it sits last, unmarked. */}
              <LegendItem
                label={t(`${T_PREFIX}.HEADING_LABEL`)}
                desc={t(`${T_PREFIX}.HEADING_DESC`)}
              />
            </Stack>
          </Stack>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {t(`${T_PREFIX}.IMAGE_NOTE`)}
        </Typography>
      </Stack>
    </PopUp>
  )
}

export default TowerLastPositionDialog
